import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';

const CartContext = createContext(null);

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState({});
  const [deliveryDates, setDeliveryDates] = useState({}); // { supplier_id: date }
  const [customProductsBySupplier, setCustomProductsBySupplier] = useState({}); // { supplier_id: [{id, name, quantity}] }

  const addToCart = useCallback((product, quantity = 1) => {
    setCart(prev => {
      const existing = prev[product.id];
      if (existing) {
        return {
          ...prev,
          [product.id]: {
            ...existing,
            quantity: existing.quantity + quantity
          }
        };
      }
      return {
        ...prev,
        [product.id]: {
          product: {
            ...product,
            supplier_id: product.supplier_id,
            supplier_name: product.supplier_name,
            supplier_delivery_days: product.supplier_delivery_days || [],
            supplier_lead_time_days: product.supplier_lead_time_days || 1,
            supplier_cutoff_hour: product.supplier_cutoff_hour || 12
          },
          quantity,
          outOfStockAction: 'replace_better'  // Valeur par défaut
        }
      };
    });
  }, []);

  const updateQuantity = useCallback((productId, quantity) => {
    if (quantity <= 0) {
      removeFromCart(productId);
      return;
    }
    setCart(prev => ({
      ...prev,
      [productId]: {
        ...prev[productId],
        quantity
      }
    }));
  }, []);

  const removeFromCart = useCallback((productId) => {
    setCart(prev => {
      const newCart = { ...prev };
      delete newCart[productId];
      return newCart;
    });
  }, []);

  const clearCart = useCallback(() => {
    setCart({});
    setDeliveryDates({});
    setCustomProductsBySupplier({});
  }, []);

  const getCartItems = useCallback(() => {
    return Object.values(cart);
  }, [cart]);

  const getCartTotal = useCallback(() => {
    return Object.values(cart).reduce((total, item) => {
      return total + ((item.product.catalog_price || item.product.unit_price || 0) * item.quantity);
    }, 0);
  }, [cart]);

  const getCartCount = useCallback(() => {
    return Object.values(cart).reduce((count, item) => count + item.quantity, 0);
  }, [cart]);

  // Group cart items by supplier
  const getCartBySupplier = useCallback(() => {
    const bySupplier = {};
    Object.values(cart).forEach(item => {
      const supplierId = item.product.supplier_id;
      if (!bySupplier[supplierId]) {
        bySupplier[supplierId] = {
          supplier_id: supplierId,
          supplier_name: item.product.supplier_name,
          supplier_delivery_days: item.product.supplier_delivery_days || [],
          supplier_lead_time_days: item.product.supplier_lead_time_days || 1,
          supplier_cutoff_hour: item.product.supplier_cutoff_hour || 12,
          items: [],
          total: 0
        };
      }
      bySupplier[supplierId].items.push(item);
      bySupplier[supplierId].total += (item.product.catalog_price || item.product.unit_price || 0) * item.quantity;
    });
    return Object.values(bySupplier);
  }, [cart]);

  // Set delivery date for a specific supplier
  const setSupplierDeliveryDate = useCallback((supplierId, date) => {
    setDeliveryDates(prev => ({
      ...prev,
      [supplierId]: date
    }));
  }, []);

  // Get delivery date for a specific supplier
  const getSupplierDeliveryDate = useCallback((supplierId) => {
    return deliveryDates[supplierId] || null;
  }, [deliveryDates]);

  // Check if all suppliers have a delivery date set
  const allDeliveryDatesSet = useMemo(() => {
    const suppliers = getCartBySupplier();
    if (suppliers.length === 0) return false;
    return suppliers.every(s => deliveryDates[s.supplier_id]);
  }, [getCartBySupplier, deliveryDates]);

  // Out of stock action management
  const setOutOfStockAction = useCallback((productId, action) => {
    setCart(prev => ({
      ...prev,
      [productId]: {
        ...prev[productId],
        outOfStockAction: action
      }
    }));
  }, []);

  const setOutOfStockActionForAll = useCallback((action) => {
    setCart(prev => {
      const updated = {};
      Object.keys(prev).forEach(productId => {
        updated[productId] = {
          ...prev[productId],
          outOfStockAction: action
        };
      });
      return updated;
    });
  }, []);

  // Custom products (non-referenced) management - per supplier
  const addCustomProduct = useCallback((supplierId, name, quantity) => {
    const id = Date.now() + Math.random(); // Unique ID
    setCustomProductsBySupplier(prev => ({
      ...prev,
      [supplierId]: [...(prev[supplierId] || []), { id, name, quantity }]
    }));
  }, []);

  const removeCustomProduct = useCallback((supplierId, id) => {
    setCustomProductsBySupplier(prev => ({
      ...prev,
      [supplierId]: (prev[supplierId] || []).filter(p => p.id !== id)
    }));
  }, []);

  const getCustomProductsForSupplier = useCallback((supplierId) => {
    return customProductsBySupplier[supplierId] || [];
  }, [customProductsBySupplier]);

  const value = {
    cart,
    deliveryDates,
    customProductsBySupplier,
    addToCart,
    updateQuantity,
    removeFromCart,
    clearCart,
    getCartItems,
    getCartTotal,
    getCartCount,
    getCartBySupplier,
    setSupplierDeliveryDate,
    getSupplierDeliveryDate,
    allDeliveryDatesSet,
    setOutOfStockAction,
    setOutOfStockActionForAll,
    addCustomProduct,
    removeCustomProduct,
    getCustomProductsForSupplier
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};
